import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { ConfirmationDialog } from '@/components/ConfirmationDialog';
import { NoteModal } from '@/components/modals/NoteModal/NoteModal';
import { STICKY_ACTIONS_COLUMN_CLASS } from '@/components/table/constants';
import { SelectionBanner } from '@/components/table/SelectionBanner';
import type {
  ColumnsConfigItem,
  SortableColumnsConfig,
} from '@/components/table/sortableColumns/types';
import { Table } from '@/components/table/Table';
import { useUpdateAsset } from '@/hooks/useAssets';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useOpenEntityDrawer } from '@/hooks/useOpenEntityDrawer';
import { useHardDeleteRisk, useUpdateRisk } from '@/hooks/useRisks';
import { AssetTag } from '@/sections/asset/components/modals/TagModal';
import { useAssetActionHandlers } from '@/sections/asset/hooks/actions';
import {
  buildColumnsForEntity,
  getQueryEntity,
} from '@/sections/insights/queryBuilder/entities';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import { useQueryData } from '@/sections/insights/queryBuilder/state/useQueryData';
import { useAuth } from '@/state/auth';
import type { Asset, GraphQuery, GraphQueryResponse } from '@/types';
import { cn } from '@/utils/classname';
import { useStorage } from '@/utils/storage/useStorage.util';

import { extractQueryResults } from './utils';

interface TableResultsProps {
  data: GraphQueryResponse;
  isFetching?: boolean;
  onInvalidate?: () => void;
}

/**
 * TableResults - Displays query results in a table with entity-specific columns
 *
 * Automatically detects entity type from GraphQueryResponse and
 * renders appropriate columns using buildColumnsForEntity.
 *
 * @example
 * // For asset data, displays Group and Identifier columns
 * <TableResults data={{ assets: [...], count: 2 }} />
 *
 * @example
 * // For risk data, falls back to Key column
 * <TableResults data={{ risks: [...], count: 2 }} />
 */
export const TableResults = ({
  data,
  isFetching,
  onInvalidate,
}: TableResultsProps) => {
  const { isGlobal, usernames } = useQueryBuilder();
  const { queryState, dispatch } = useQueryData();
  const { isPraetorianUser } = useAuth();
  const { openDrawerByKey } = useOpenEntityDrawer();
  const { mutateAsync: updateRisk } = useUpdateRisk();
  const { mutateAsync: hardDeleteRisk, isPending: isPurgingRisk } =
    useHardDeleteRisk();
  const { mutateAsync: updateAsset } = useUpdateAsset();
  const assetHandlers = useAssetActionHandlers();

  // Modal state
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  // Praetorian-only hard-delete confirmation target. Non-null opens the
  // destructive ConfirmationDialog; the purge fires only on confirm.
  const [riskToPurge, setRiskToPurge] = useState<{
    key: string;
    name?: string;
  } | null>(null);

  // Column settings state with persistence
  // Single key intentionally shared: entity-type changes are detected in the render
  // body below and reset via setActiveColumnIds(undefined) before any render
  const [activeColumnIds, setActiveColumnIds] = useStorage<
    string[] | undefined
  >({ key: 'sortableColumn_query-results' }, undefined);

  // Extract data array and entity type
  const results = useMemo(() => extractQueryResults(data), [data]);

  // Track entity type to reset column state when query type changes
  const entityTypeRef = useRef<string | undefined>(undefined);
  const currentEntityType = results?.type;
  if (entityTypeRef.current !== currentEntityType) {
    entityTypeRef.current = currentEntityType;
    if (activeColumnIds !== undefined) {
      setActiveColumnIds(undefined); // Reset to "all columns" for new entity type
    }
  }

  // Build columns based on detected entity type
  const columns = useMemo(
    () =>
      results
        ? buildColumnsForEntity(
            results.type,
            isGlobal,
            usernames,
            isPraetorianUser
          )
        : [],
    [results, isGlobal, usernames, isPraetorianUser]
  );

  // Build sortable columns config for gear icon
  // Only includes columns with non-empty unique IDs (these are user-toggleable)
  const sortableColumnsConfig: SortableColumnsConfig = useMemo(() => {
    const seen = new Set<string>();
    const active: ColumnsConfigItem[] = [];

    for (const col of columns) {
      const id = col.id ? String(col.id) : '';
      if (id && !seen.has(id)) {
        seen.add(id);
        active.push({
          id,
          label: typeof col.label === 'string' ? col.label : id,
        });
      }
    }

    return { fixed: [], active, available: [] };
  }, [columns]);

  // Filter columns based on user's active column selection
  // Columns with empty ID are always included (not toggleable)
  const visibleColumns = useMemo(() => {
    const activeSet = new Set(
      activeColumnIds ?? sortableColumnsConfig.active.map(c => c.id)
    );
    return columns.filter(col => {
      const id = col.id ? String(col.id) : '';
      return !id || activeSet.has(id);
    });
  }, [columns, activeColumnIds, sortableColumnsConfig.active]);

  // Extract data array for table (cast to satisfy Table component type requirements)
  const tableData = useMemo(() => {
    if (!results) return [];
    return results.data as ((typeof results.data)[0] & {
      children?: unknown[];
    })[];
  }, [results]);

  // Row selection management — uses useBulkSelection for Gmail-style two-step selection
  const bulkSelection = useBulkSelection({
    data: tableData as Array<{ key: string }>,
    activeFilters: {} as GraphQuery,
  });

  const {
    selectedRows,
    setSelectedRows,
    clearSelection,
    isSelectAll,
    isAllLoadedSelected,
    selectAllMatching,
    selectedCount,
  } = bulkSelection;

  // Build row actions for each row based on entity type
  // Uses adapter pattern: entity.buildRowActions wraps existing main table action builders
  const rowActions = useMemo(() => {
    // No row actions in global mode (same behavior as row click - line 125)
    if (isGlobal) return undefined;
    if (!results) return undefined;

    const entity = getQueryEntity(results.type);
    if (!entity?.buildRowActions) return undefined;

    const handlers: import('@/sections/insights/queryBuilder/entities/types').QueryActionHandlers =
      {
        openDrawer: openDrawerByKey,
        showToast: (message: string) => toast.success(message),
        showError: (message: string) => toast.error(message),
        isPraetorianUser,
        invalidate: onInvalidate,
        updateRisk,
        // Request-only: open the destructive dialog; purge fires on confirm.
        requestHardDeleteRisk: risk => setRiskToPurge(risk),
        assetHandlers,
        onCommentClick: (asset: Asset) => {
          setSelectedAsset(asset);
          setIsNoteModalOpen(true);
        },
        onTagClick: (asset: Asset) => {
          setSelectedAsset(asset);
          setIsTagModalOpen(true);
        },
      };

    return (row: unknown) => entity.buildRowActions!(row, handlers);
  }, [
    results,
    isGlobal,
    openDrawerByKey,
    isPraetorianUser,
    updateRisk,
    assetHandlers,
    onInvalidate,
  ]);

  const handleRowClick = (row: unknown) => {
    const rowData = row as Record<string, unknown>;

    if (isGlobal) {
      toast.error(
        'Table rows are not clickable in global query mode. Please assume into a specific account to review full item details.'
      );
      return;
    }

    if (!rowData.key) {
      toast.error('Unable to open details: missing entity key');
      return;
    }

    let key = String(rowData.key);
    if (key && key.startsWith('#port')) {
      key = String(rowData.source);
    } else if (key && key.startsWith('#webpage')) {
      key = '#webapplication#' + String(rowData.url);
    }

    // Use the key-based drawer opening (auto-detects entity type from key prefix)
    openDrawerByKey(key);
  };

  return (
    <div data-testid="query-table-view" className="h-full">
      <p className="pb-4 pl-4">
        <i>
          Fetched {tableData.length} result{tableData.length !== 1 ? 's' : ''}.
        </i>
      </p>
      <SelectionBanner
        selectedCount={selectedCount}
        totalLoadedCount={tableData.length}
        hasNextPage={false}
        isSelectAll={isSelectAll}
        isAllLoadedSelected={isAllLoadedSelected}
        resourceLabel="results"
        onSelectAllMatching={selectAllMatching}
        onClearSelection={clearSelection}
      />
      <Table
        name="query-results"
        tableClassName={cn(
          STICKY_ACTIONS_COLUMN_CLASS,
          '[&_th:last-child]:bg-chariot-background-secondary'
        )}
        columns={visibleColumns}
        data={tableData}
        status="success"
        isFetching={isFetching}
        error={null}
        onRowClick={handleRowClick}
        rowActions={rowActions}
        selection={{
          value: selectedRows,
          onChange: setSelectedRows,
          selectAll: true,
        }}
        {...(sortableColumnsConfig.active.length > 0
          ? {
              sortableColumns: {
                activeColumns:
                  activeColumnIds ??
                  sortableColumnsConfig.active.map(c => c.id),
                setActiveColumns: setActiveColumnIds,
                defaultConfig: sortableColumnsConfig,
              },
            }
          : {})}
        {...(columns.length > 0 && tableData.length > 0
          ? {
              sort: {
                orderBy: (queryState?.metadata.orderBy || '') as keyof unknown,
                descending: queryState?.metadata.descending ?? false,
                onSort: (newSort: {
                  orderBy: keyof unknown;
                  descending: boolean;
                }) => {
                  dispatch({
                    type: 'UPDATE_METADATA',
                    metadata: {
                      orderBy: String(newSort.orderBy),
                      descending: newSort.descending,
                    },
                    skipDirty: true,
                  });
                },
              },
            }
          : {})}
      />

      {/* NoteModal for comment action */}
      {selectedAsset && (
        <NoteModal
          open={isNoteModalOpen}
          onClose={() => {
            setIsNoteModalOpen(false);
            setSelectedAsset(null);
          }}
          onSubmit={async (note: string) => {
            if (selectedAsset) {
              try {
                await updateAsset({
                  key: selectedAsset.key,
                  name: selectedAsset.name || selectedAsset.key,
                  comment: note,
                  status: selectedAsset.status,
                });
                setIsNoteModalOpen(false);
                setSelectedAsset(null);
              } catch {
                // useUpdateAsset handles error display via its onError handler
                // Modal stays open so user can retry
              }
            }
          }}
          resource={{
            key: selectedAsset?.key || '',
            name: selectedAsset?.name || selectedAsset?.key || '',
          }}
          resourceType="Asset"
          mode="simple"
          presentation="modal"
        />
      )}

      {/* TagModal for tag action */}
      {selectedAsset && (
        <AssetTag
          open={isTagModalOpen}
          onClose={() => {
            setIsTagModalOpen(false);
            setSelectedAsset(null);
          }}
          assetKeys={[selectedAsset.key]}
          onSuccess={() => {
            setIsTagModalOpen(false);
            setSelectedAsset(null);
          }}
        />
      )}

      {/* Hard-delete confirmation (Praetorian-only). Copy mirrors the main
          Vulnerabilities table's destructive dialog verbatim; the purge mutation
          + invalidation fire only on confirm — never on the menu click. */}
      <ConfirmationDialog
        open={riskToPurge !== null}
        onClose={() => setRiskToPurge(null)}
        onConfirm={async () => {
          const target = riskToPurge;
          setRiskToPurge(null);
          if (target) {
            try {
              await hardDeleteRisk({ key: target.key, name: target.name });
              onInvalidate?.();
            } catch {
              // useHardDeleteRisk already toasted the failure via
              // toast.promise. Radix's confirm button closes the dialog
              // unconditionally regardless of this handler's outcome (see
              // SeedRowActions.tsx handleHardDelete), so this catch exists
              // solely to keep the rejected purge from surfacing as an
              // unhandled promise rejection.
            }
          }
        }}
        title="Delete finding permanently?"
        description="This permanently erases the finding and cannot be undone — no audit trail is kept. To close a finding with a reason and preserve the audit trail, use Reject instead. Reject is recommended for normal triage."
        variant="destructive"
        confirmLabel="Delete Permanently"
        loadingLabel="Deleting..."
        isLoading={isPurgingRisk}
        data-testid="query-results-hard-delete-dialog"
      />
    </div>
  );
};
